import { useState, useEffect, useCallback } from 'react'
import { globalActivityTrackerService } from '../services/GlobalActivityTrackerService'
import { WorkoutConfirmationData, WorkoutType } from '../plugins/ActivityTrackerPlugin'
import { UploadStats } from '../services/ActivityUploadService'

/**
 * Hook to access global activity tracker state from any component
 * This ensures workout events are handled regardless of which view is active
 */
export const useGlobalActivityTracker = () => {
  const [state, setState] = useState(globalActivityTrackerService.getState())

  useEffect(() => {
    // Subscribe to global state updates
    const unsubscribe = globalActivityTrackerService.subscribe((newState) => {
      setState(newState)
    })

    // Cleanup subscription on unmount
    return unsubscribe
  }, [])

  // Helper functions to access specific state
  const isVisible = state.isVisible
  const isLoading = state.isLoading
  const error = state.error
  const successMessage = state.successMessage
  const confirmationData = state.confirmationData
  const isConfirmationRequired = state.isConfirmationRequired
  const uploadStats = state.uploadStats

  // Helper function to clear messages
  const clearMessages = useCallback(() => {
    // Note: This would need to be implemented in the global service
    // For now, we'll just log that we want to clear messages
    console.log('🔧 useGlobalActivityTracker: Clear messages requested')
  }, [])

  // Workout confirmation methods
  const confirmWorkout = useCallback(async (title: string, type: WorkoutType) => {
    await globalActivityTrackerService.confirmWorkout(title, type)
  }, [])

  const discardWorkout = useCallback(async () => {
    await globalActivityTrackerService.discardWorkout()
  }, [])

  return {
    isVisible,
    isLoading,
    error,
    successMessage,
    confirmationData,
    isConfirmationRequired,
    uploadStats,
    clearMessages,
    confirmWorkout,
    discardWorkout,
  }
}
